import React, { useCallback, useEffect, useMemo, useState } from "react";

/**
 * CareLogix™ ORACLE — Sports Responsible-Gaming Guardrails (Phase 26E).
 *
 * Default-blocked global jurisdiction matrix + 21+ age confirmation +
 * deposit/loss limits + self-exclusion + no-chasing-losses warning.
 *
 * Behavior:
 *   • Renders <gate /> if the user has not satisfied requirements (default).
 *     - If satisfied → renders `children`.
 *   • All state is persisted in localStorage under `carelogix_sports_rg_v1`.
 *   • This is a research/intelligence guardrail only — ORACLE does NOT place
 *     wagers and never accepts bet execution payloads.
 */

const LS_KEY = "carelogix_sports_rg_v1";

// Default-blocked global jurisdiction matrix.
// US: explicit "legal" list per public state-by-state sports-betting status
// (research only; the user must self-confirm legal status). Default = blocked.
// International placeholders included; treat as "blocked unless user opts in".
export const US_STATES = [
  { code: "AL", name: "Alabama", allowed: false },
  { code: "AK", name: "Alaska", allowed: false },
  { code: "AZ", name: "Arizona", allowed: true },
  { code: "AR", name: "Arkansas", allowed: true },
  { code: "CA", name: "California", allowed: false },
  { code: "CO", name: "Colorado", allowed: true },
  { code: "CT", name: "Connecticut", allowed: true },
  { code: "DE", name: "Delaware", allowed: true },
  { code: "FL", name: "Florida", allowed: true },
  { code: "GA", name: "Georgia", allowed: false },
  { code: "HI", name: "Hawaii", allowed: false },
  { code: "ID", name: "Idaho", allowed: false },
  { code: "IL", name: "Illinois", allowed: true },
  { code: "IN", name: "Indiana", allowed: true },
  { code: "IA", name: "Iowa", allowed: true },
  { code: "KS", name: "Kansas", allowed: true },
  { code: "KY", name: "Kentucky", allowed: true },
  { code: "LA", name: "Louisiana", allowed: true },
  { code: "ME", name: "Maine", allowed: true },
  { code: "MD", name: "Maryland", allowed: true },
  { code: "MA", name: "Massachusetts", allowed: true },
  { code: "MI", name: "Michigan", allowed: true },
  { code: "MN", name: "Minnesota", allowed: false },
  { code: "MS", name: "Mississippi", allowed: true },
  { code: "MO", name: "Missouri", allowed: true },
  { code: "MT", name: "Montana", allowed: true },
  { code: "NE", name: "Nebraska", allowed: true },
  { code: "NV", name: "Nevada", allowed: true },
  { code: "NH", name: "New Hampshire", allowed: true },
  { code: "NJ", name: "New Jersey", allowed: true },
  { code: "NM", name: "New Mexico", allowed: true },
  { code: "NY", name: "New York", allowed: true },
  { code: "NC", name: "North Carolina", allowed: true },
  { code: "ND", name: "North Dakota", allowed: false },
  { code: "OH", name: "Ohio", allowed: true },
  { code: "OK", name: "Oklahoma", allowed: false },
  { code: "OR", name: "Oregon", allowed: true },
  { code: "PA", name: "Pennsylvania", allowed: true },
  { code: "RI", name: "Rhode Island", allowed: true },
  { code: "SC", name: "South Carolina", allowed: false },
  { code: "SD", name: "South Dakota", allowed: true },
  { code: "TN", name: "Tennessee", allowed: true },
  { code: "TX", name: "Texas", allowed: false },
  { code: "UT", name: "Utah", allowed: false },
  { code: "VT", name: "Vermont", allowed: true },
  { code: "VA", name: "Virginia", allowed: true },
  { code: "WA", name: "Washington", allowed: true },
  { code: "WV", name: "West Virginia", allowed: true },
  { code: "WI", name: "Wisconsin", allowed: true },
  { code: "WY", name: "Wyoming", allowed: true },
  { code: "DC", name: "Washington, D.C.", allowed: true },
];

export const INTERNATIONAL = [
  { code: "GB", name: "United Kingdom", allowed: true, age_min: 18 },
  { code: "CA", name: "Canada", allowed: true, age_min: 19 },
  { code: "AU", name: "Australia", allowed: true, age_min: 18 },
  { code: "DE", name: "Germany", allowed: true, age_min: 18 },
  { code: "ES", name: "Spain", allowed: true, age_min: 18 },
  { code: "FR", name: "France", allowed: true, age_min: 18 },
  { code: "IT", name: "Italy", allowed: true, age_min: 18 },
  { code: "NL", name: "Netherlands", allowed: true, age_min: 24 },
  { code: "ZA", name: "South Africa", allowed: true, age_min: 18 },
  { code: "NG", name: "Nigeria", allowed: true, age_min: 18 },
  { code: "KE", name: "Kenya", allowed: true, age_min: 18 },
  { code: "GH", name: "Ghana", allowed: true, age_min: 18 },
  { code: "JP", name: "Japan", allowed: false, age_min: 20 },
  { code: "CN", name: "China", allowed: false, age_min: 18 },
  { code: "IN", name: "India", allowed: false, age_min: 18 },
  { code: "AE", name: "United Arab Emirates", allowed: false, age_min: 18 },
  { code: "SA", name: "Saudi Arabia", allowed: false, age_min: 18 },
];

const DEFAULT_STATE = {
  region: "US",        // "US" | "INTL"
  jurisdiction: "",     // e.g., "NJ" or "GB"
  ageConfirmed: false,
  dailyDepositCapUsd: 200,
  weeklyLossCapUsd: 400,
  cooloffUntilTs: 0,
  selfExcludedUntilTs: 0,
  acceptedRgTerms: false,
  ackChasingLosses: false,
  unlocked: false,
};

function loadState() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    return { ...DEFAULT_STATE, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

function saveState(s) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

function isJurisdictionAllowed(region, code) {
  if (!code) return false;
  if (region === "US") {
    const row = US_STATES.find((s) => s.code === code);
    return !!row?.allowed;
  }
  const row = INTERNATIONAL.find((s) => s.code === code);
  return !!row?.allowed;
}

function ageMinForJurisdiction(region, code) {
  if (region === "US") return 21;
  const row = INTERNATIONAL.find((s) => s.code === code);
  return row?.age_min || 21;
}

function fmtCountdown(ts) {
  if (!ts || ts < Date.now()) return null;
  const ms = ts - Date.now();
  const hrs = Math.floor(ms / 3_600_000);
  const days = Math.floor(hrs / 24);
  if (days >= 1) return `${days}d ${hrs % 24}h`;
  return `${hrs}h`;
}

/**
 * @param {{
 *   children: React.ReactNode,
 *   variant?: "block"|"warning",
 *   onUnlock?: () => void,
 * }} props
 */
export default function SportsResponsibleGamingGate({ children, onUnlock }) {
  const [s, setS] = useState(loadState);
  const [step, setStep] = useState(0); // 0 region · 1 juris · 2 age · 3 limits · 4 terms

  useEffect(() => saveState(s), [s]);

  const allowed = useMemo(() => isJurisdictionAllowed(s.region, s.jurisdiction), [s.region, s.jurisdiction]);
  const ageMin = useMemo(() => ageMinForJurisdiction(s.region, s.jurisdiction), [s.region, s.jurisdiction]);
  const cooloff = fmtCountdown(s.cooloffUntilTs);
  const selfExclude = fmtCountdown(s.selfExcludedUntilTs);

  const passed =
    s.unlocked &&
    s.region &&
    s.jurisdiction &&
    allowed &&
    s.ageConfirmed &&
    s.acceptedRgTerms &&
    s.ackChasingLosses &&
    !cooloff &&
    !selfExclude;

  useEffect(() => {
    if (passed && onUnlock) onUnlock();
  }, [passed, onUnlock]);

  const setCooloff = useCallback((hours) => {
    const ts = Date.now() + hours * 3_600_000;
    setS((x) => ({ ...x, cooloffUntilTs: ts }));
  }, []);

  const setSelfExclude = useCallback((days) => {
    const ts = Date.now() + days * 86_400_000;
    setS((x) => ({ ...x, selfExcludedUntilTs: ts }));
  }, []);

  const reset = useCallback(() => {
    setS({ ...DEFAULT_STATE });
    setStep(0);
  }, []);

  // Backfill `unlocked` for users with legacy persisted state.
  useEffect(() => {
    if (
      !s.unlocked &&
      s.region &&
      s.jurisdiction &&
      allowed &&
      s.ageConfirmed &&
      s.acceptedRgTerms &&
      s.ackChasingLosses &&
      !cooloff &&
      !selfExclude
    ) {
      // Auto-lift for users from previous build who already accepted everything.
      setS((x) => ({ ...x, unlocked: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (passed) {
    return (
      <div data-testid="sports-rg-passed-shell">
        <SportsStatusStrip
          state={s}
          ageMin={ageMin}
          onReset={reset}
          onCooloff={setCooloff}
          onSelfExclude={setSelfExclude}
        />
        {children}
      </div>
    );
  }

  if (cooloff) {
    return (
      <CoolOffPanel
        kind="cooloff"
        until={s.cooloffUntilTs}
        onClear={() => setS((x) => ({ ...x, cooloffUntilTs: 0 }))}
      />
    );
  }
  if (selfExclude) {
    return (
      <CoolOffPanel
        kind="selfExclude"
        until={s.selfExcludedUntilTs}
        onClear={() => setS((x) => ({ ...x, selfExcludedUntilTs: 0 }))}
      />
    );
  }

  return (
    <div style={C.wrap} data-testid="sports-rg-gate" role="region" aria-label="Sports responsible-gaming gate">
      <header style={C.head}>
        <span style={C.chip}>21+</span>
        <div style={{ flex: 1 }}>
          <strong style={C.title}>Sports Intelligence · Responsible-Gaming Gate</strong>
          <p style={C.subtitle}>
            Default <b>blocked</b>. Confirm jurisdiction, age, and limits to access research-only
            sports intelligence. ORACLE never places wagers and never offers guaranteed picks.
          </p>
        </div>
      </header>

      <div style={C.stepper} role="tablist" aria-label="Gate steps">
        {["Region", "Jurisdiction", "Age", "Limits", "Terms"].map((label, i) => (
          <button
            key={label}
            type="button"
            onClick={() => setStep(i)}
            role="tab"
            aria-selected={step === i}
            style={{
              ...C.stepBtn,
              color: i <= step ? "#c5a059" : "#475569",
              borderBottomColor: step === i ? "#c5a059" : "transparent",
            }}
            data-testid={`sports-rg-step-${i}`}
          >
            <span style={C.stepBullet}>{i + 1}</span>
            <span>{label}</span>
          </button>
        ))}
      </div>

      <div style={C.body}>
        {step === 0 && (
          <div data-testid="sports-rg-step-region-body">
            <p style={C.bodyText}>Where are you accessing from?</p>
            <div style={C.btnRow}>
              <button
                type="button"
                onClick={() => { setS((x) => ({ ...x, region: "US", jurisdiction: "" })); setStep(1); }}
                style={{ ...C.primary, ...(s.region === "US" ? C.primaryActive : {}) }}
                data-testid="sports-rg-region-US"
              >🇺🇸 United States</button>
              <button
                type="button"
                onClick={() => { setS((x) => ({ ...x, region: "INTL", jurisdiction: "" })); setStep(1); }}
                style={{ ...C.primary, ...(s.region === "INTL" ? C.primaryActive : {}) }}
                data-testid="sports-rg-region-INTL"
              >🌍 International</button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="sports-rg-step-juris-body">
            <p style={C.bodyText}>
              Choose your {s.region === "US" ? "US state" : "country"}. Default-blocked unless legal.
            </p>
            <select
              value={s.jurisdiction}
              onChange={(e) => setS((x) => ({ ...x, jurisdiction: e.target.value }))}
              style={C.select}
              data-testid="sports-rg-jurisdiction-select"
              aria-label="Jurisdiction"
            >
              <option value="">— Select —</option>
              {(s.region === "US" ? US_STATES : INTERNATIONAL).map((j) => (
                <option key={j.code} value={j.code} data-testid={`sports-rg-juris-opt-${j.code}`}>
                  {j.name} {j.allowed ? "✓" : "⛔ blocked"}
                </option>
              ))}
            </select>
            {s.jurisdiction && !allowed && (
              <div style={C.warn} data-testid="sports-rg-juris-blocked">
                ⛔ Sports intelligence is not available in this jurisdiction. Choose a legal
                jurisdiction or close this module.
              </div>
            )}
            {allowed && (
              <div style={C.ok} data-testid="sports-rg-juris-allowed">
                ✓ Jurisdiction recognized. Minimum age: <b>{ageMin}+</b>.
              </div>
            )}
            <div style={C.btnRow}>
              <button type="button" onClick={() => setStep(0)} style={C.ghost}>Back</button>
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={!allowed}
                style={{ ...C.primary, opacity: allowed ? 1 : 0.45, cursor: allowed ? "pointer" : "not-allowed" }}
                data-testid="sports-rg-juris-next"
              >Next</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="sports-rg-step-age-body">
            <p style={C.bodyText}>
              You must be at least <b>{ageMin}</b> years old to view sports intelligence in this jurisdiction.
            </p>
            <label style={C.checkRow}>
              <input
                type="checkbox"
                checked={s.ageConfirmed}
                onChange={(e) => setS((x) => ({ ...x, ageConfirmed: e.target.checked }))}
                data-testid="sports-rg-age-confirm"
              />
              <span>I confirm I am {ageMin}+ and meet all local legal requirements.</span>
            </label>
            <div style={C.btnRow}>
              <button type="button" onClick={() => setStep(1)} style={C.ghost}>Back</button>
              <button
                type="button"
                onClick={() => setStep(3)}
                disabled={!s.ageConfirmed}
                style={{ ...C.primary, opacity: s.ageConfirmed ? 1 : 0.45, cursor: s.ageConfirmed ? "pointer" : "not-allowed" }}
                data-testid="sports-rg-age-next"
              >Next</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="sports-rg-step-limits-body">
            <p style={C.bodyText}>
              Set responsible-gaming limits. These are discipline guardrails — ORACLE does not place
              wagers. Use them to plan bankroll and avoid chasing losses.
            </p>
            <div style={C.fieldGrid}>
              <label style={C.lbl}>
                <span>Daily deposit cap (USD)</span>
                <input
                  type="number"
                  min="0"
                  step="25"
                  value={s.dailyDepositCapUsd}
                  onChange={(e) => setS((x) => ({ ...x, dailyDepositCapUsd: Math.max(0, parseFloat(e.target.value || "0")) }))}
                  style={C.input}
                  data-testid="sports-rg-daily-cap"
                />
              </label>
              <label style={C.lbl}>
                <span>Weekly loss cap (USD)</span>
                <input
                  type="number"
                  min="0"
                  step="50"
                  value={s.weeklyLossCapUsd}
                  onChange={(e) => setS((x) => ({ ...x, weeklyLossCapUsd: Math.max(0, parseFloat(e.target.value || "0")) }))}
                  style={C.input}
                  data-testid="sports-rg-weekly-cap"
                />
              </label>
            </div>
            <div style={C.btnRow}>
              <button type="button" onClick={() => setStep(2)} style={C.ghost}>Back</button>
              <button
                type="button"
                onClick={() => setStep(4)}
                style={C.primary}
                data-testid="sports-rg-limits-next"
              >Next</button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div data-testid="sports-rg-step-terms-body">
            <p style={C.bodyText}>Acknowledge the responsible-gaming principles to continue.</p>
            <label key="rg-terms-row" style={C.checkRow} htmlFor="sports-rg-accept-terms">
              <input
                id="sports-rg-accept-terms"
                type="checkbox"
                checked={s.acceptedRgTerms}
                onChange={(e) => {
                  const v = e.target.checked;
                  setS((x) => ({ ...x, acceptedRgTerms: v }));
                }}
                data-testid="sports-rg-accept-terms"
              />
              <span>
                I accept that sports intelligence is research/education only, not financial advice or
                a guaranteed-profit service. <b>No autonomous wagering.</b> Human review required.
              </span>
            </label>
            <label key="rg-chasing-row" style={C.checkRow} htmlFor="sports-rg-ack-chasing">
              <input
                id="sports-rg-ack-chasing"
                type="checkbox"
                checked={s.ackChasingLosses}
                onChange={(e) => {
                  const v = e.target.checked;
                  setS((x) => ({ ...x, ackChasingLosses: v }));
                }}
                data-testid="sports-rg-ack-chasing"
              />
              <span>
                I acknowledge that chasing losses, exceeding deposit caps, or wagering money I cannot
                afford to lose is harmful. I will use cool-off and self-exclusion if needed.
              </span>
            </label>
            <div style={C.btnRow}>
              <button type="button" onClick={() => setStep(3)} style={C.ghost}>Back</button>
              <button
                type="button"
                onClick={() => {
                  setS((x) => ({ ...x, unlocked: true }));
                  if (onUnlock) onUnlock();
                }}
                disabled={!(s.acceptedRgTerms && s.ackChasingLosses)}
                style={{
                  ...C.primary,
                  opacity: s.acceptedRgTerms && s.ackChasingLosses ? 1 : 0.45,
                  cursor: s.acceptedRgTerms && s.ackChasingLosses ? "pointer" : "not-allowed",
                }}
                data-testid="sports-rg-unlock"
              >Unlock research feed</button>
            </div>
          </div>
        )}

        <div style={C.helpRow}>
          <button type="button" onClick={() => setCooloff(24)} style={C.warnBtn} data-testid="sports-rg-cooloff-24h">
            Cool-off · 24h
          </button>
          <button type="button" onClick={() => setCooloff(72)} style={C.warnBtn} data-testid="sports-rg-cooloff-72h">
            Cool-off · 72h
          </button>
          <button type="button" onClick={() => setSelfExclude(30)} style={C.dangerBtn} data-testid="sports-rg-self-exclude-30">
            Self-Exclude · 30 days
          </button>
          <a
            href="https://www.ncpgambling.org/help-treatment/national-helpline-1-800-522-4700/"
            target="_blank"
            rel="noopener noreferrer"
            style={C.helpLink}
            data-testid="sports-rg-helpline"
          >
            Help: 1-800-522-4700 (NCPG)
          </a>
        </div>
      </div>
    </div>
  );
}

function SportsStatusStrip({ state, ageMin, onReset, onCooloff, onSelfExclude }) {
  return (
    <div style={C.statusStrip} data-testid="sports-rg-status">
      <span style={C.statusChip}>✓ {state.region === "US" ? "USA" : "INTL"} · {state.jurisdiction} · {ageMin}+</span>
      <span style={C.statusMeta}>Daily cap: ${state.dailyDepositCapUsd} · Weekly loss cap: ${state.weeklyLossCapUsd}</span>
      <div style={C.statusActions}>
        <button type="button" onClick={() => onCooloff(24)} style={C.tinyWarn} data-testid="sports-rg-status-cooloff">Cool-off 24h</button>
        <button type="button" onClick={() => onSelfExclude(30)} style={C.tinyDanger} data-testid="sports-rg-status-self-exclude">Self-Exclude</button>
        <button type="button" onClick={onReset} style={C.tinyGhost} data-testid="sports-rg-status-reset">Reset</button>
      </div>
    </div>
  );
}

function CoolOffPanel({ kind, until, onClear }) {
  const remaining = fmtCountdown(until) || "—";
  const isExclude = kind === "selfExclude";
  return (
    <div style={C.coolWrap} data-testid={`sports-rg-${kind}-panel`} role="alert">
      <h3 style={C.coolTitle}>
        {isExclude ? "Self-Exclusion Active" : "Cool-Off Active"} · {remaining}
      </h3>
      <p style={C.coolBody}>
        Sports intelligence is intentionally locked. We strongly recommend you complete the cool-off
        period. If you need support, please contact the National Council on Problem Gambling helpline
        (1-800-522-4700) or your local responsible-gaming service.
      </p>
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        <a
          href="https://www.ncpgambling.org/help-treatment/national-helpline-1-800-522-4700/"
          target="_blank"
          rel="noopener noreferrer"
          style={C.helpLink}
        >
          Help: 1-800-522-4700 (NCPG)
        </a>
        <button type="button" onClick={onClear} style={C.tinyGhost} data-testid={`sports-rg-${kind}-clear`}>
          I'm OK — remove block
        </button>
      </div>
    </div>
  );
}

const C = {
  wrap: {
    margin: "12px 0",
    background: "rgba(11,15,20,0.96)",
    border: "1px solid rgba(197,160,89,0.30)",
    borderRadius: 14,
    padding: 16,
    color: "#e2e8f0",
    fontFamily: "'Figtree', 'Inter', system-ui, sans-serif",
  },
  head: { display: "flex", alignItems: "center", gap: 12, marginBottom: 10 },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #c5a059, #ffd966)",
    color: "#0c1a10",
    fontWeight: 900,
    fontFamily: "'Space Mono', monospace",
    letterSpacing: "0.05em",
  },
  title: { color: "#fde68a", fontSize: 16, display: "block" },
  subtitle: { color: "#94a3b8", fontSize: 12.5, marginTop: 4, lineHeight: 1.5 },
  stepper: { display: "flex", gap: 10, borderBottom: "1px solid rgba(255,255,255,0.06)", marginTop: 8, overflowX: "auto" },
  stepBtn: {
    appearance: "none",
    background: "transparent",
    border: "none",
    borderBottom: "2px solid",
    cursor: "pointer",
    padding: "10px 4px",
    color: "#475569",
    fontFamily: "'Space Mono', monospace",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.06em",
    display: "flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
  },
  stepBullet: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: "rgba(197,160,89,0.18)",
    color: "#c5a059",
    fontSize: 10,
    fontWeight: 800,
  },
  body: { paddingTop: 14, display: "flex", flexDirection: "column", gap: 12 },
  bodyText: { color: "#cbd5e1", fontSize: 13, lineHeight: 1.5, margin: 0 },
  btnRow: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 },
  primary: {
    appearance: "none",
    background: "linear-gradient(135deg, #c5a059, #ffd966)",
    border: "1px solid #c5a059",
    color: "#0c1a10",
    padding: "9px 16px",
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "'Space Mono', monospace",
    fontSize: 11.5,
    fontWeight: 800,
    letterSpacing: "0.06em",
  },
  primaryActive: { boxShadow: "0 0 0 3px rgba(197,160,89,0.30)" },
  ghost: {
    appearance: "none",
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.18)",
    color: "#cbd5e1",
    padding: "8px 14px",
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "'Space Mono', monospace",
    fontSize: 11,
  },
  select: {
    appearance: "none",
    background: "#040608",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "#e2e8f0",
    padding: "10px 12px",
    borderRadius: 10,
    fontSize: 13,
    fontFamily: "'Figtree', 'Inter', system-ui, sans-serif",
    width: "100%",
  },
  warn: {
    background: "rgba(239,68,68,0.10)",
    border: "1px solid rgba(239,68,68,0.30)",
    color: "#fecaca",
    padding: "8px 10px",
    borderRadius: 8,
    fontSize: 12.5,
  },
  ok: {
    background: "rgba(16,185,129,0.10)",
    border: "1px solid rgba(16,185,129,0.30)",
    color: "#d1fae5",
    padding: "8px 10px",
    borderRadius: 8,
    fontSize: 12.5,
  },
  checkRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    color: "#cbd5e1",
    fontSize: 13,
    lineHeight: 1.5,
  },
  fieldGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 },
  lbl: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    color: "#94a3b8",
    fontFamily: "'Space Mono', monospace",
    fontSize: 10.5,
    letterSpacing: "0.06em",
  },
  input: {
    appearance: "none",
    background: "#040608",
    border: "1px solid rgba(255,255,255,0.06)",
    color: "#e2e8f0",
    padding: "8px 10px",
    borderRadius: 8,
    fontSize: 13,
    fontFamily: "'Figtree', 'Inter', system-ui, sans-serif",
  },
  helpRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 14,
    paddingTop: 12,
    borderTop: "1px dashed rgba(255,255,255,0.08)",
    alignItems: "center",
  },
  warnBtn: {
    appearance: "none",
    background: "rgba(245,158,11,0.10)",
    border: "1px solid rgba(245,158,11,0.45)",
    color: "#fcd34d",
    padding: "6px 10px",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "'Space Mono', monospace",
    fontSize: 11,
  },
  dangerBtn: {
    appearance: "none",
    background: "rgba(239,68,68,0.10)",
    border: "1px solid rgba(239,68,68,0.55)",
    color: "#fecaca",
    padding: "6px 10px",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "'Space Mono', monospace",
    fontSize: 11,
  },
  helpLink: {
    color: "#67e8f9",
    fontFamily: "'Space Mono', monospace",
    fontSize: 11,
    letterSpacing: "0.04em",
    textDecoration: "underline",
  },
  statusStrip: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    alignItems: "center",
    padding: "8px 12px",
    background: "rgba(16,185,129,0.06)",
    border: "1px solid rgba(16,185,129,0.30)",
    borderRadius: 10,
    margin: "10px 0",
    fontFamily: "'Space Mono', monospace",
    fontSize: 11,
  },
  statusChip: {
    color: "#10b981",
    fontWeight: 800,
    letterSpacing: "0.08em",
  },
  statusMeta: { color: "#94a3b8", flex: 1, minWidth: 140 },
  statusActions: { display: "flex", gap: 6, flexWrap: "wrap" },
  tinyWarn: {
    appearance: "none",
    background: "rgba(245,158,11,0.08)",
    border: "1px solid rgba(245,158,11,0.35)",
    color: "#fcd34d",
    padding: "3px 8px",
    borderRadius: 6,
    fontSize: 10,
    cursor: "pointer",
    fontFamily: "'Space Mono', monospace",
  },
  tinyDanger: {
    appearance: "none",
    background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.40)",
    color: "#fecaca",
    padding: "3px 8px",
    borderRadius: 6,
    fontSize: 10,
    cursor: "pointer",
    fontFamily: "'Space Mono', monospace",
  },
  tinyGhost: {
    appearance: "none",
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.18)",
    color: "#94a3b8",
    padding: "3px 8px",
    borderRadius: 6,
    fontSize: 10,
    cursor: "pointer",
    fontFamily: "'Space Mono', monospace",
  },
  coolWrap: {
    margin: "12px 0",
    padding: 16,
    background: "rgba(239,68,68,0.06)",
    border: "1px solid rgba(239,68,68,0.40)",
    borderRadius: 14,
    color: "#fecaca",
    fontFamily: "'Figtree', 'Inter', system-ui, sans-serif",
  },
  coolTitle: { margin: 0, color: "#fecaca" },
  coolBody: { margin: "8px 0 0", lineHeight: 1.5, fontSize: 13 },
};
